import { CustomButton } from "@/components/ui/Buttons";
import CalenderIcon from "@/assets/icons/Calender.svg";
import { useGetTopEventsQuery } from "@/redux/feature/calender/calenderApi";
import { TopEventType } from "./EventTypes";
import ProductCard from "../ProductCard";
import { useState } from "react";
import { VideoData } from "@/redux/feature/videoDashboard/videoDashboardType";
import VideoPlayerV3 from "@/components/ui/VideoPlayerV3";

type GoToAdCalenderProps = {
  productId: string;
};

const GoToAdCalender = ({ productId }: GoToAdCalenderProps) => {
  const { data, isLoading } = useGetTopEventsQuery({
    productId: productId,
  });

  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<VideoData | null>(null);

  if (isLoading) {
    return (
      <div className="border border-[#E4E7EC] p-4 rounded-lg mb-[38px]">
        <p className="text-gray-500 text-sm">Loading upcoming event...</p>
      </div>
    );
  }

  // Data comes as a single object, not an array
  const topEvent: TopEventType | undefined = data;

  if (topEvent?.message === "No upcoming events with videos")
    return <NoUpcomingEvents />;

  if (!topEvent) return <NoUpcomingEvents />;

  return (
    <div className="border border-[#E4E7EC] p-4 rounded-lg mb-[38px]">
      {/* Tag */}
      <p className="text-[10px] text-[#000000] tracking-[100%] bg-[#F1F1F1] w-fit py-[0.5px] px-[3.5px] rounded-full">
        Upcoming Event
      </p>

      {/* Date + Days left */}
      <div className="flex items-center justify-between my-3">
        <h4 className="text-[#777777] font-medium text-[15px] leading-[100%] tracking-[0%]">
          {new Date(topEvent.date).toLocaleDateString("en-US", {
            day: "2-digit",
            month: "long",
          })}
        </h4>
        <p className="text-[#006DBA] bg-[#EDF7FF] text-xs leading-[125%] tracking-[-0.75%] px-[10.5px] py-[5px] rounded-[10px]">
          in <span className="font-semibold">{topEvent.days_until} days</span>
        </p>
      </div>

      {/* Event name */}
      <h3 className="text-2xl leading-[100%] tracking-[0%] mb-2 font-medium">
        {topEvent.event}
      </h3>

      {/* Logos */}
      <div className="flex items-center mb-3">
        {topEvent?.brands?.list.map((brand, index) => (
          <div
            key={index}
            className={`w-10 h-10 rounded-full overflow-hidden border-2 border-white ${
              index !== 0 ? "-ml-4" : ""
            }`}
          >
            {brand.logo ? (
              <img
                src={brand.logo}
                alt={brand.name}
                className="w-full h-full object-cover"
                onError={(e) => (e.currentTarget.style.display = "none")}
              />
            ) : (
              <div className="w-full h-full bg-gray-300 flex items-center justify-center"></div>
            )}
          </div>
        ))}

        {/* Show extra count if any */}
        {(topEvent?.brands?.count ?? 0) >
          (topEvent?.brands?.list?.length ?? 0) && (
          <div className="w-10 h-10 rounded-full border-2 border-orange-500 flex items-center justify-center bg-white -ml-4">
            <span className="text-[#FF6600] text-[15px] font-semibold">
              {(topEvent?.brands?.count ?? 0) -
                (topEvent?.brands?.list?.length ?? 0)}
              +
            </span>
          </div>
        )}
      </div>

      <p className="text-[#777777] text-[12px] leading-[100%] tracking-[0%]">
        Companies have started campaign
      </p>

      {topEvent.sample_video && (
        <div
          className={`h-40 sm:h-52 relative bg-gray-200 flex-shrink-0 mt-3 rounded-lg`}
        >
          <VideoPlayerV3
            thumbnailUrl={topEvent.sample_video.thumbnail_url}
            url={topEvent.sample_video.video_url}
            currentlyPlaying={currentlyPlaying}
            setCurrentlyPlaying={setCurrentlyPlaying}
            className="w-full h-full object-cover rounded-lg"
          />
        </div>
      )}

      {/* CTA button */}
      <a
        href="#calendar"
        className="block mt-5"
        onClick={(e) => {
          e.preventDefault();
          document
            .getElementById("calendar")
            ?.scrollIntoView({ behavior: "smooth" });
        }}
      >
        <CustomButton
          variant="outline"
          color="#000000"
          borderRadius="8px"
          border="1px solid #E4E7EC"
          fontSize="14px"
          fontWeight="500"
          padding="6px"
          className="flex items-center justify-center gap-x-3 w-full"
        >
          <CalenderIcon />
          Go to ad calendar
        </CustomButton>
      </a>
    </div>
  );
};

export default GoToAdCalender;

// Fallback Component
const NoUpcomingEvents = () => (
  <div className="flex flex-col items-center justify-center py-12 text-center border border-[#E4E7EC] p-4 rounded-lg mb-[38px]">
    <div className="w-20 h-20 flex items-center justify-center bg-gray-100 rounded-full mb-4 text-4xl">
      📭
    </div>
    <h4 className="text-lg font-semibold text-gray-700 mb-2">
      No Upcoming Events
    </h4>
    <p className="text-gray-500 max-w-sm">
      There are currently no upcoming events for the selected brand or product.
      Check back later or try selecting a different filter.
    </p>
    <a href="#calendar" className="block mt-5">
      <CustomButton
        variant="outline"
        color="#000000"
        borderRadius="8px"
        border="1px solid #E4E7EC"
        fontSize="14px"
        fontWeight="500"
        padding="6px"
        className="flex items-center justify-center gap-x-3 w-full"
      >
        <CalenderIcon />
        Go to ad calendar
      </CustomButton>
    </a>
  </div>
);
